import React from "react";

import { Box, Collapse, IconButton, TableCell, TableCellProps, TableRow, useTheme } from "@mui/material";
import { Row } from "@tanstack/react-table";

import { HoverRow } from "./HoverRow";

type CollapsibleContextType = { open: boolean; setOpen: React.Dispatch<React.SetStateAction<boolean>> };
const CollapsibleContext = React.createContext<CollapsibleContextType>({
    open: false,
    setOpen: () => null,
});

export const CollapsibleRow = ({ children }: React.PropsWithChildren<object>) => {
    const [open, setOpen] = React.useState(false);

    return <CollapsibleContext.Provider value={{ open, setOpen }}>{children}</CollapsibleContext.Provider>;
};

type CollapsibleSummaryProps<T> = React.PropsWithChildren<TableCellProps> & {
    row: Row<T>;
    disabled?: boolean;
};
export const CollapsibleSummary = <T,>({ children, disabled, row, ...props }: CollapsibleSummaryProps<T>) => {
    const context = React.useContext(CollapsibleContext);

    if (!context) {
        throw new Error("CollapsibleSummary have to be use inside CollapsibleContext");
    }

    return (
        <HoverRow {...row}>
            <TableCell align="center" style={{ width: 60, padding: 0, borderBottom: "none" }} {...props}>
                <IconButton onClick={() => context.setOpen((prev) => !prev)} size="large" disabled={disabled}>
                    {context.open ? (
                        <i className="fa-solid fa-chevron-down" style={{ fontSize: 16 }} />
                    ) : (
                        <i className="fa-solid fa-chevron-right" style={{ fontSize: 16 }} />
                    )}
                </IconButton>
            </TableCell>
            {children}
        </HoverRow>
    );
};
export const CollapsibleDetails = (props: React.PropsWithChildren<object>) => {
    const context = React.useContext(CollapsibleContext);
    const theme = useTheme();

    if (!context) {
        throw new Error("CollapsibleDetails have to be use inside CollapsibleContext");
    }

    return (
        <TableRow>
            <TableCell style={{ padding: 0 }} colSpan={12}>
                <Collapse in={context.open} collapsedSize={0} style={{ paddingLeft: theme.spacing(15) }}>
                    <Box style={{ padding: theme.spacing(4), paddingTop: 0 }}>{props.children}</Box>
                </Collapse>
            </TableCell>
        </TableRow>
    );
};

export const CollapsibleRows = ({ children }: React.PropsWithChildren<object>) => {
    const context = React.useContext(CollapsibleContext);

    if (!context) {
        throw new Error("CollapsibleRows have to be used inside CollapsibleContext");
    }
    if (!context.open) {
        return null;
    }
    return <>{children}</>;
};
